import React, { useState, useEffect } from 'react';
import ReactModal from 'react-modal';
import Select from 'react-select';
import notifications from '../../notifications';
import { updateProgram, addProgram, getProgramById } from '../../api/programs';
import { getProgramSessions } from '../../api/admin';
import { ORG_NAMES, SEASONS, PROGRAM_TYPES, PROGRAM_STATUSES, SPORTS } from '../../constants/constants';
import RosterTab from './RosterTab';
import SessionsTab from './SessionsTab';
import './ProgramEditModal.css';

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const seasonOptions = SEASONS.map(s => ({ value: s.toLowerCase(), label: s }));
const typeOptions = PROGRAM_TYPES.map(t => ({ value: t, label: t.charAt(0).toUpperCase() + t.slice(1) }));
const statusOptions = PROGRAM_STATUSES.map(s => ({ value: s, label: s.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) }));
const sportOptions = SPORTS.map(s => ({ value: s, label: s.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') }));
const orgOptions = Object.entries(ORG_NAMES).map(([key, label]) => ({ value: key, label }));

const ProgramEditModal = ({ program, coaches, onClose, onSuccess, mode = 'edit' }) => {
  const isAddMode = mode === 'add';
  const [activeTab, setActiveTab] = useState('details');

  // Parse skippedDates string into array
  const parseSkippedDates = (dates) => {
    if (!dates) return [];
    if (Array.isArray(dates)) return dates.filter(d => d);
    return dates.split(';').map(d => d.trim()).filter(d => d);
  };

  const [copyFromId, setCopyFromId] = useState('');
  const [isCopying, setIsCopying] = useState(false);
  const [formData, setFormData] = useState({
    title: program.title || '',
    sport: program.sport || '',
    type: program.type || '',
    org: program.org || '',
    year: program.year || new Date().getFullYear(),
    season: program.season || '',
    startDate: program.startDate || '',
    endDate: program.endDate || '',
    startTime: program.startTime || '',
    endTime: program.endTime || '',
    daysOfWeek: Array.isArray(program.daysOfWeek) ? program.daysOfWeek : [],
    location: program.location || '',
    locationDetails: program.locationDetails || '',
    minAge: program.minAge || '',
    maxAge: program.maxAge || '',
    minKidCount: program.minKidCount || '',
    maxKidCount: program.maxKidCount || '',
    price: program.price || '',
    status: program.status || '',
    director: program.director || '',
    coaches: program.coaches || [],
    notes: program.notes || '',
    registrationUrl: program.registrationUrl || '',
    externalId: program.externalId || ''
  });
  const [sendEmails, setSendEmails] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [skippedDates, setSkippedDates] = useState(parseSkippedDates(program.skippedDates));
  const [hasSessions, setHasSessions] = useState(false);
  const originalDirector = program.director;

  const addSkippedDate = () => {
    setSkippedDates([...skippedDates, '']);
  };

  const removeSkippedDate = (index) => {
    setSkippedDates(skippedDates.filter((_, i) => i !== index));
  };

  const updateSkippedDate = (index, value) => {
    const updated = [...skippedDates];
    updated[index] = value;
    setSkippedDates(updated);
  };

  // Auto-check sendEmails and set status to confirmed when assigning a director for the first time
  useEffect(() => {
    if (!originalDirector && formData.director) {
      setSendEmails(true);
      setFormData(prev => ({ ...prev, status: 'confirmed' }));
    }
  }, [formData.director, originalDirector]);

  // Check if sessions exist for this program (edit mode only)
  useEffect(() => {
    if (isAddMode || !program.id) return;
    getProgramSessions(program.id).then(res => {
      if (res?.data?.length > 0) setHasSessions(true);
    });
  }, [program.id]);

  const coachOptions = coaches
    .map(c => ({
      value: c.id,
      label: `${c.firstName} ${c.lastName}`
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSelectChange = (name) => (option) => {
    setFormData(prev => ({
      ...prev,
      [name]: option?.value || ''
    }));
  };

  const handleDayToggle = (day) => {
    setFormData(prev => {
      const updated = prev.daysOfWeek.includes(day)
        ? prev.daysOfWeek.filter(d => d !== day)
        : [...prev.daysOfWeek, day];
      return { ...prev, daysOfWeek: updated.sort((a, b) => DAYS.indexOf(a) - DAYS.indexOf(b)) };
    });
  };

  const handleCopyFromId = async () => {
    if (!copyFromId.trim()) {
      notifications.notifyError('Please enter a program ID to copy from');
      return;
    }
    setIsCopying(true);
    try {
      const response = await getProgramById(copyFromId.trim());
      if (response.error || !response.data) {
        notifications.notifyError('Failed to fetch program. Check the ID and try again.');
        return;
      }
      const sourceProgram = response.data;
      setFormData({
        title: sourceProgram.title || '',
        sport: sourceProgram.sport || '',
        type: sourceProgram.type || '',
        org: sourceProgram.org || '',
        year: sourceProgram.year || new Date().getFullYear(),
        season: sourceProgram.season || '',
        startDate: sourceProgram.startDate || '',
        endDate: sourceProgram.endDate || '',
        startTime: sourceProgram.startTime || '',
        endTime: sourceProgram.endTime || '',
        daysOfWeek: Array.isArray(sourceProgram.daysOfWeek) ? sourceProgram.daysOfWeek : [],
        location: sourceProgram.location || '',
        locationDetails: sourceProgram.locationDetails || '',
        minAge: sourceProgram.minAge || '',
        maxAge: sourceProgram.maxAge || '',
        minKidCount: sourceProgram.minKidCount || '',
        maxKidCount: sourceProgram.maxKidCount || '',
        price: sourceProgram.price || '',
        status: '',
        director: '',
        coaches: [],
        notes: sourceProgram.notes || '',
        registrationUrl: sourceProgram.registrationUrl || ''
      });
      setSkippedDates(parseSkippedDates(sourceProgram.skippedDates));
      notifications.notifySuccess('Program data copied successfully');
    } catch (error) {
      notifications.notifyError('An error occurred while copying the program');
    } finally {
      setIsCopying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const programData = {
        ...formData,
        year: Number(formData.year),
        skippedDates: skippedDates.filter(d => d).join('; '),
        minAge: formData.minAge ? Number(formData.minAge) : undefined,
        maxAge: formData.maxAge ? Number(formData.maxAge) : undefined,
        minKidCount: formData.minKidCount ? Number(formData.minKidCount) : undefined,
        maxKidCount: formData.maxKidCount ? Number(formData.maxKidCount) : undefined,
        price: formData.price ? Number(formData.price) : undefined
      };

      if (isAddMode) {
        programData.vendor = 'skyhawks';
        const response = await addProgram(programData);
        if (!response.error && response.data) {
          notifications.notifySuccess('Program created successfully');
          onSuccess(response.data);
        } else {
          notifications.notifyError('Failed to create program');
        }
      } else {
        const response = await updateProgram(program.id, programData, sendEmails);
        if (!response.error) {
          notifications.notifySuccess('Program updated successfully');
          onSuccess({ ...program, ...formData });
        } else {
          notifications.notifyError('Failed to update program');
        }
      }
    } catch (error) {
      notifications.notifyError(`An error occurred while ${isAddMode ? 'creating' : 'updating'} the program`);
    } finally {
      setIsLoading(false);
    }
  };

  const findOption = (options, value) => options.find(o => o.value === value) || null;

  return (
    <ReactModal
      isOpen={true}
      onRequestClose={onClose}
      shouldCloseOnOverlayClick={true}
      contentLabel="Edit Program"
      ariaHideApp={false}
      className="program-edit-modal-content"
      overlayClassName="modal-overlay"
    >
      <div className="program-edit-modal">
        <button className="close-button" onClick={onClose}>×</button>
        <h2>{isAddMode ? 'Add Program' : 'Edit Program'}</h2>

        {/* Tab Navigation - only show in edit mode */}
        {!isAddMode && (
          <div className="modal-tabs">
            <button
              type="button"
              className={`tab-button ${activeTab === 'details' ? 'active' : ''}`}
              onClick={() => setActiveTab('details')}
            >
              Program Details
            </button>
            <button
              type="button"
              className={`tab-button ${activeTab === 'roster' ? 'active' : ''}`}
              onClick={() => setActiveTab('roster')}
            >
              Roster ({program.registrationCount || 0})
            </button>
            <button
              type="button"
              className={`tab-button ${activeTab === 'sessions' ? 'active' : ''}`}
              onClick={() => setActiveTab('sessions')}
            >
              Sessions
            </button>
          </div>
        )}

        {/* Tab Content */}
        {activeTab === 'roster' && !isAddMode ? (
          <RosterTab
            programId={program.id}
            programOrg={program.org}
          />
        ) : activeTab === 'sessions' && !isAddMode ? (
          <SessionsTab
            program={program}
            coaches={coaches}
          />
        ) : (
        <form onSubmit={handleSubmit} className="program-edit-form">
          {isAddMode && (
            <div className="form-section copy-section">
              <h3>Copy from Existing</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Program ID (optional)</label>
                  <div className="copy-input-row">
                    <input
                      type="text"
                      value={copyFromId}
                      onChange={(e) => setCopyFromId(e.target.value)}
                      placeholder="Enter program ID to copy from"
                    />
                    <button
                      type="button"
                      className="copy-btn"
                      onClick={handleCopyFromId}
                      disabled={isCopying}
                    >
                      {isCopying ? 'Copying...' : 'Copy'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="form-section">
            <h3>Basic Info</h3>
            {!isAddMode && (
              <div className="form-row">
                <div className="form-group">
                  <label>ID</label>
                  <input
                    type="text"
                    value={program.id}
                    disabled
                    style={{ backgroundColor: '#f5f5f5', cursor: 'not-allowed' }}
                  />
                </div>
              </div>
            )}
            <div className="form-row">
              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>External ID</label>
                <input
                  type="text"
                  name="externalId"
                  value={formData.externalId}
                  onChange={handleChange}
                  placeholder="External system ID"
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group full-width">
                <label>Registration URL</label>
                <div className="link-input-row">
                  <input
                    type="text"
                    name="registrationUrl"
                    value={formData.registrationUrl}
                    onChange={handleChange}
                    placeholder="https://..."
                  />
                  <button
                    type="button"
                    className="open-link-btn"
                    onClick={() => window.open(formData.registrationUrl, '_blank')}
                    disabled={!formData.registrationUrl}
                  >
                    Open
                  </button>
                </div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Sport</label>
                <Select
                  value={findOption(sportOptions, formData.sport)}
                  onChange={handleSelectChange('sport')}
                  options={sportOptions}
                  isClearable
                />
              </div>
              <div className="form-group">
                <label>Type</label>
                <Select
                  value={findOption(typeOptions, formData.type)}
                  onChange={handleSelectChange('type')}
                  options={typeOptions}
                  isClearable
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Organization</label>
                <Select
                  value={findOption(orgOptions, formData.org)}
                  onChange={handleSelectChange('org')}
                  options={orgOptions}
                  isClearable
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Year</label>
                <input
                  type="number"
                  name="year"
                  value={formData.year}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Season</label>
                <Select
                  value={findOption(seasonOptions, formData.season?.toLowerCase())}
                  onChange={handleSelectChange('season')}
                  options={seasonOptions}
                  isClearable
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Schedule</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Start Date</label>
                <input
                  type="date"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>End Date</label>
                <input
                  type="date"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Start Time</label>
                <input
                  type="time"
                  name="startTime"
                  value={formData.startTime}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>End Time</label>
                <input
                  type="time"
                  name="endTime"
                  value={formData.endTime}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group full-width">
                <label>Days of Week</label>
                <div className="days-of-week-checkboxes">
                  {DAYS.map(day => (
                    <label key={day} className="day-checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.daysOfWeek.includes(day)}
                        onChange={() => handleDayToggle(day)}
                      />
                      {day}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group full-width">
                <label>Skipped Dates</label>
                <div className="multi-date-selector">
                  {skippedDates.map((date, index) => (
                    <div key={index} className="date-input-row">
                      <input
                        type="date"
                        value={date}
                        onChange={(e) => updateSkippedDate(index, e.target.value)}
                      />
                      <button
                        type="button"
                        className="remove-date-btn"
                        onClick={() => removeSkippedDate(index)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    className="add-date-btn"
                    onClick={addSkippedDate}
                  >
                    + Add Date
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Location</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Location</label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Location Details</label>
                <input
                  type="text"
                  name="locationDetails"
                  value={formData.locationDetails}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Participants</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Min Age</label>
                <input
                  type="number"
                  name="minAge"
                  value={formData.minAge}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Max Age</label>
                <input
                  type="number"
                  name="maxAge"
                  value={formData.maxAge}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Min Kid Count</label>
                <input
                  type="number"
                  name="minKidCount"
                  value={formData.minKidCount}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>Max Kid Count</label>
                <input
                  type="number"
                  name="maxKidCount"
                  value={formData.maxKidCount}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Price</label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  step="0.01"
                />
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Status & Staff</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Status</label>
                <Select
                  value={findOption(statusOptions, formData.status)}
                  onChange={handleSelectChange('status')}
                  options={statusOptions}
                  isClearable
                />
              </div>
              <div className="form-group">
                <label>Director</label>
                <Select
                  value={findOption(coachOptions, formData.director)}
                  onChange={handleSelectChange('director')}
                  options={coachOptions}
                  isClearable
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group full-width">
                <label>Coaches (Assistants)</label>
                <Select
                  value={coachOptions.filter(opt => formData.coaches?.includes(opt.value))}
                  onChange={(selected) => setFormData(prev => ({
                    ...prev,
                    coaches: selected ? selected.map(s => s.value) : []
                  }))}
                  options={coachOptions}
                  isMulti
                  isClearable
                  placeholder="Select assistant coaches..."
                  isDisabled={hasSessions}
                />
                {hasSessions && (
                  <p className="field-hint">Coach assignments are managed per-session. Use the Sessions tab to add or remove coaches.</p>
                )}
              </div>
            </div>
          </div>

          <div className="form-section">
            <h3>Notes</h3>
            <div className="form-row">
              <div className="form-group full-width">
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Additional notes..."
                />
              </div>
            </div>
          </div>

          {!isAddMode && (
            <div className="form-section">
              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={sendEmails}
                    onChange={(e) => setSendEmails(e.target.checked)}
                  />
                  Send notification emails to enrolled participants
                </label>
              </div>
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="cancel-button" onClick={onClose} disabled={isLoading}>
              Cancel
            </button>
            <button type="submit" className="submit-button" disabled={isLoading}>
              {isLoading ? (isAddMode ? 'Creating...' : 'Saving...') : (isAddMode ? 'Create Program' : 'Save Changes')}
            </button>
          </div>
        </form>
        )}
      </div>
    </ReactModal>
  );
};

export default ProgramEditModal;
